import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import {
  ChartContainer,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  formatNumber,
  formatPrice,
  formatPercentage,
  getTreemapData,
  generateInsights,
  processAgencyData,
} from './AgencyAnalysisChart.utils'
import {
  AgencyAnalysisChartProps,
  AgencyAnalysisChartRef,
  ViewType,
} from './AgencyAnalysisChart.types'

const VIEW_TYPES: { type: ViewType; labelKey: string }[] = [
  { type: 'listings', labelKey: 'agencyAnalysis.viewTypes.byListings' },
  { type: 'value', labelKey: 'agencyAnalysis.viewTypes.byValue' },
  { type: 'regions', labelKey: 'agencyAnalysis.viewTypes.byRegions' },
]

const MIN_LISTINGS_OPTIONS = [
  { value: 1, labelKey: 'agencyAnalysis.minListings.all' },
  { value: 10, labelKey: 'agencyAnalysis.minListings.10plus' },
  { value: 50, labelKey: 'agencyAnalysis.minListings.50plus' },
  { value: 100, labelKey: 'agencyAnalysis.minListings.100plus' },
  { value: 500, labelKey: 'agencyAnalysis.minListings.500plus' },
] as const

export const AgencyAnalysisChart = forwardRef<AgencyAnalysisChartRef, AgencyAnalysisChartProps>(
  (
    {
      data = {
        agencies: [],
        totalListings: 0,
        totalAgencies: 0,
        marketLeader: null,
        top5Share: 0,
      },
      loading = false,
      error = null,
      onMinListingsChange,
      minListings = 1,
      showInsights = true,
      showControls = true,
      t = (key: string) => key,
    },
    ref
  ) => {
    const [viewType, setViewType] = useState<ViewType>('listings')
    const chartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<echarts.ECharts | null>(null)

    useEffect(() => {
      if (!loading && !error && processedData.agencies.length > 0) {
        createChart()
      }

      return () => {
        chartInstance.current?.dispose()
      }
    }, [data, viewType, loading, error])

    const processedData = processAgencyData(data, minListings)

    const insights = showInsights ? generateInsights(processedData, false, t) : []

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (chartInstance.current) {
          chartInstance.current.resize()
          return chartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => [],
      getInsights: () => insights,
    }))

    const createChart = () => {
      if (!chartRef.current) return

      if (chartInstance.current) {
        chartInstance.current.dispose()
      }

      chartInstance.current = safeChartInit(chartRef.current)

      const treeData = getTreemapData(processedData.agencies, viewType, t)

      const option: any = {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            if (!params.data.data) {
              return `<strong>${params.name}</strong><br/>
                   Total: ${formatNumber(params.value)}`
            }

            const agency = params.data.data
            const label =
              viewType === 'listings'
                ? t('agencyAnalysis.tooltip.listings')
                : viewType === 'value'
                  ? t('agencyAnalysis.tooltip.marketValue')
                  : t('agencyAnalysis.tooltip.regionalCoverage')

            return `<strong>${params.name}</strong><br/>
                 ${label}: ${formatNumber(params.value)}<br/>
                 Listings: ${formatNumber(agency.listingCount)}<br/>
                 Market Share: ${formatPercentage(agency.marketShare)}<br/>
                 Avg Price: ${formatPrice(Math.round(agency.avgPrice))}<br/>
                 Regions: ${agency.regionCount}`
          },
        },
        series: [
          {
            type: 'treemap',
            data: [treeData],
            leafDepth: 2,
            roam: 'move',
            label: {
              show: true,
              formatter: (params: any) => {
                // Adjust threshold based on view type
                let threshold = 1
                if (viewType === 'regions') {
                  threshold = 1 // Show labels for agencies with 1+ regions
                } else if (viewType === 'value') {
                  threshold = 1 // Show labels for agencies with 1M+ value
                }

                if (params.value < threshold) return ''
                return params.name
              },
              fontSize: 12,
            },
            upperLabel: {
              show: true,
              height: 30,
              fontSize: 14,
              fontWeight: 'bold',
            },
            levels: [
              {
                itemStyle: {
                  borderColor: '#fff',
                  borderWidth: 4,
                  gapWidth: 4,
                },
              },
              {
                itemStyle: {
                  borderColor: '#fff',
                  borderWidth: 2,
                  gapWidth: 2,
                },
              },
            ],
            breadcrumb: {
              show: true,
              top: '10',
              left: '10',
            },
          },
        ],
      }

      chartInstance.current.setOption(option)
    }

    // Handle window resize
    useEffect(() => {
      const handleResize = () => {
        chartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('agencyAnalysis.loading')}
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    return (
      <PrintWrapper>
        <Box marginBottom="30px">
          <ChartSection>
            <Box marginBottom="24px">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap="16px"
              >
                <ChartTitle>{t('agencyAnalysis.charts.marketShareByAgency')}</ChartTitle>
                <Box display="flex" gap="16px" flexWrap="wrap" alignItems="center">
                  {showControls && (
                    <ButtonGroup>
                      {VIEW_TYPES.map((vt) => (
                        <Button
                          key={vt.type}
                          variant={viewType === vt.type ? 'primary' : 'secondary'}
                          size="small"
                          onClick={() => setViewType(vt.type)}
                        >
                          {t(vt.labelKey)}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                  <ButtonGroup>
                    {MIN_LISTINGS_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        variant={minListings === option.value ? 'primary' : 'secondary'}
                        size="small"
                        onClick={() => onMinListingsChange(option.value)}
                      >
                        {t(option.labelKey)}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              </Box>
            </Box>
            {processedData.agencies.length === 0 ? (
              <NotAvailable
                title={t('agencyAnalysis.noData.title')}
                description={t('agencyAnalysis.noData.description')}
              />
            ) : null}
            <ChartContainer height={500} ref={chartRef} />
          </ChartSection>
        </Box>

        {insights.length > 0 && (
          <Box>
            <ChartTitle style={{ marginBottom: '24px' }}>{t('shared.keyInsights')}</ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 300px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

AgencyAnalysisChart.displayName = 'AgencyAnalysisChart'
